import axios from "../../api/axiosInstance";
import { STATUS, extractErrorInfo, getProjectId } from "./sharedfunction";

/**
 * Get organizations based on user role and permissions
 * Super Admin: Returns all organizations in the system
 * Other roles: Returns only the user's own organization
 * @returns {Promise<Object>} Response with status, data (organizations array), and message
 */
export async function getOrganizations() {
  try {
    const projectId = getProjectId();
    let url = `${process.env.REACT_APP_API_URL}/organization/get`;
    if (projectId) {
      url += `?projectId=${projectId}`;
    }

    const response = await axios.get(
      url,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data?.organizations || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching organizations:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Create a new organization with a unique name and optional agent service URL
 * @param {Object} params - Organization creation parameters
 * @param {string} params.name - Unique name for the organization (3-100 characters)
 * @param {string} [params.agentServiceUrl] - Optional URL for the organization's agent service
 * @param {boolean} [params.mfaEnabled=true] - Whether MFA is required for organization users
 * @returns {Promise<Object>} Response with status, data (organization object), and message
 */
export async function createOrganization({ name, agentServiceUrl, mfaEnabled = true } = {}) {
  try {
    if (!name || name.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Organization name is required",
      };
    }

    if (name.trim().length < 3) {
      return {
        status: STATUS.ERROR,
        error: "Organization name must be at least 3 characters",
      };
    }

    const payload = {
      name: name.trim(),
      agentServiceUrl: agentServiceUrl || null,
      mfaEnabled,
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/organization/create`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data?.organization || response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error creating organization:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update an existing organization's name, agent service URL, and/or MFA setting
 * @param {Object} params - Organization update parameters
 * @param {string} params.id - Organization ID to update (UUID format)
 * @param {string} params.name - New unique name for the organization (3-100 characters)
 * @param {string} [params.agentServiceUrl] - New agent service URL (can be null to remove)
 * @param {boolean} [params.mfaEnabled] - Whether MFA is required for organization users
 * @returns {Promise<Object>} Response with status and message
 */
export async function updateOrganization({ id, name, agentServiceUrl, mfaEnabled } = {}) {
  try {
    if (!id || id.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Organization ID is required",
      };
    }

    if (!name || name.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Organization name is required",
      };
    }

    if (name.trim().length < 3) {
      return {
        status: STATUS.ERROR,
        error: "Organization name must be at least 3 characters",
      };
    }

    const payload = {
      id: id.trim(),
      name: name.trim(),
      agentServiceUrl: agentServiceUrl || null,
      mfaEnabled,
    };

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/organization/update`,
      payload,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error updating organization:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get the agent service URL for the currently authenticated user's organization
 * @returns {Promise<Object>} Response with status, data (agentServiceUrl), and message
 */
export async function getOrganizationServiceUrl() {
  try {
    const projectId = getProjectId();
    let url = `${process.env.REACT_APP_API_URL}/organization/getServiceUrl`;
    if (projectId) {
      url += `?projectId=${projectId}`;
    }

    const response = await axios.get(
      url,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching organization service URL:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Delete one or more organizations
 * Note: This endpoint is not in the OpenAPI spec but is used in the application
 * @param {Object} params - Delete parameters
 * @param {Array<string>} params.ids - Array of organization IDs to delete
 * @returns {Promise<Object>} Response with status and message
 */
export async function deleteOrganizations({ ids } = {}) {
  try {
    if (!ids || !Array.isArray(ids) || ids.length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Organization IDs are required",
      };
    }

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/organization/delete`,
      {
        data: { ids },
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error deleting organizations:", error);
    return extractErrorInfo(error);
  }
}

